import {
  useMatrixColumnsContext,
  useMatrixDistributionContext,
  useMatrixPrepackContext,
  useMatrixSettingsContext,
} from '@app/components/products/ProductMatrix/context';
import { MatrixHelpIcon } from '@app/components/products/ProductMatrix/MatrixHelp';
import { cn } from '@app/utils/cn';
import { useDebouncedCallback, useResizeObserver } from '@app/utils/hooks';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { DistributionControls } from '../DistributionControls';
import { TotalsToggle } from '../TotalsToggle';

const MIN_CONTROLS_SPACING = 28;

const getOffsetWidth = (element: React.MutableRefObject<HTMLElement | null>) => element.current?.offsetWidth ?? 0;

export const ControlsHeader = () => {
  const { canPinColumns } = useMatrixColumnsContext();
  const { isDesktop, isReadOnly } = useMatrixSettingsContext();
  const { shouldHidePrepackSwitch } = useMatrixPrepackContext();
  const { shouldHideDistribution } = useMatrixDistributionContext();
  const { t } = useTranslation(['orders']);

  const headerRef = React.useRef<HTMLDivElement>(null);
  const oppositeContainerRef = React.useRef<HTMLDivElement>(null);
  const totalsToggleRef = React.useRef<HTMLButtonElement>(null);

  const maxTotalsToggleWidth = React.useRef(getOffsetWidth(totalsToggleRef));
  const maxOppositeContainerWidth = React.useRef(getOffsetWidth(oppositeContainerRef));
  const [headerWidth, setHeaderWidth] = React.useState(getOffsetWidth(headerRef));

  const [isTotalsLabelCollapsed, setIsTotalsLabelCollapsed] = React.useState(false);

  const updateOverflow = React.useCallback(() => {
    setHeaderWidth(getOffsetWidth(headerRef));
  }, []);

  useResizeObserver(headerRef, useDebouncedCallback(updateOverflow));

  React.useLayoutEffect(() => {
    if (!canPinColumns) {
      setIsTotalsLabelCollapsed(false);

      return;
    }

    const currentTotalsToggleWidth = getOffsetWidth(totalsToggleRef);
    const oppositeContainer = getOffsetWidth(oppositeContainerRef);

    maxTotalsToggleWidth.current = Math.max(maxTotalsToggleWidth.current, currentTotalsToggleWidth);
    maxOppositeContainerWidth.current = Math.max(maxOppositeContainerWidth.current, oppositeContainer);

    const controlsFittingBase = headerWidth - maxOppositeContainerWidth.current;

    const areControlsFittingWithExpandedTotals = controlsFittingBase - maxTotalsToggleWidth.current > MIN_CONTROLS_SPACING;
    setIsTotalsLabelCollapsed(!areControlsFittingWithExpandedTotals);
  }, [headerWidth, canPinColumns, isTotalsLabelCollapsed]);

  return (
    <div ref={headerRef} className="grid gap-2">
      <div
        className={cn({
          'flex h-8 items-center': (!shouldHideDistribution || !shouldHidePrepackSwitch) && !isDesktop,
          'my-1': canPinColumns,
        })}
      >
        <DistributionControls />
        {isDesktop && <TotalsToggle isLabelCollapsed={isTotalsLabelCollapsed} ref={totalsToggleRef} />}
      </div>
      {!isDesktop && (
        <span className="mb-2 flex">
          {!isReadOnly && (
            <span
              ref={oppositeContainerRef}
              className="inline-flex items-center gap-1 whitespace-nowrap text-left uppercase text-neutral-60 body-6"
            >
              {t('orders:fill_in_the_units_below')} <MatrixHelpIcon />
            </span>
          )}
          <TotalsToggle isLabelCollapsed={isTotalsLabelCollapsed} ref={totalsToggleRef} />
        </span>
      )}
    </div>
  );
};
